import React, { useRef, useEffect, useState } from "react";
import { Button, IconButton, Typography } from "@mui/material";
import { Grid } from "@material-ui/core";
import { styled } from "@mui/material/styles";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { AutocompleteInput, ModalPopup } from "_components";
import { materialsymbolsupload, materialsymbolsdownload, wg_report_logo } from "images";
import { alertActions } from "_store";
import { Delete } from "images";
import { ERR_MSG } from "constants/labels";
import { formatThousands, stripCommas } from "./ForecastDrvUtils";


// ─── Constants ────────────────────────────────────────────────────────────────

const TABLE_FIELDS = [
  { key: "forecastedTemp", label: "Forecasted Temp °F", allowDecimal: true },
  { key: "totalDRV", label: "Total DRV Sendout" },
  { key: "cspFirm", label: "CSP Firm" },
  { key: "cspInterruptible", label: "CSP Interruptible" },
  { key: "wglSendout", label: "WGL Sendout" },
];

const SECTION_LABELS = {
  curtailment: "SECTION: BALANCING CURTAILMENT",
  interruption: "SECTION: SCHEDULED INTERRUPTION",
  table: "SECTION: DRV FORECAST TABLE",
};

const UPLOAD_MODE_OPTIONS = [
  { label: "Email Attachment", value: "emails_attachment" },
  { label: "DRV Forecast", value: "forecast_drv" },
];

const supportedFormats = [".xlsx", ".xls", ".xlsm"];
const emailAttachmentFormats = [".xlsx", ".xls", ".xlsm", ".pdf"];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const header = "Forecast DRV";

// ── Darker text for disabled/read-only fields ──────────────────────────────
// AutocompleteInput renders an MUI input internally, so rather than
// touching its source we target the real DOM class (.Mui-disabled) via a
// descendant selector on a wrapping element. WebkitTextFillColor is
// included because Chrome/Safari paint disabled input text using that
// property instead of `color`.
const disabledFieldSx = {
  "& .Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseTemplateDateTime = (raw) => {
  if (!raw) return null;
  const str = String(raw).trim();
  if (!str) return null;
  const parsed = dayjs(str, "hh:mm A dddd MM-DD-YYYY");
  if (parsed.isValid()) return parsed;
  const fallback = dayjs(str);
  return fallback.isValid() ? fallback : null;
};

// Lenient Gas Day parser for the DRV Forecast Table column — tries the
// template's exact MM/DD/YYYY format first, then falls back to a general
// parse so a re-saved/Excel-reformatted date (e.g. "5/1/2026") still works
// instead of silently dropping the whole table section.
const parseGasDay = (raw) => {
  const str = String(raw ?? "").trim();
  if (!str) return null;
  const strict = dayjs(str, "MM/DD/YYYY", true);
  if (strict.isValid()) return strict;
  const fallback = dayjs(str);
  return fallback.isValid() ? fallback : null;
};

const formatDateTime = (date) => {
  if (!date) return "";
  return dayjs(date).format("hh:mm A dddd MM-DD-YYYY");
};

// ─── Excel styling helpers ────────────────────────────────────────────────────

const styleHeaderRow = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A3A5C" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFD6E4F0" } },
      bottom: { style: "thin", color: { argb: "FFD6E4F0" } },
      left: { style: "thin", color: { argb: "FFD6E4F0" } },
      right: { style: "thin", color: { argb: "FFD6E4F0" } },
    };
  });
};

const styleSectionLabelRow = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, italic: true, color: { argb: "FF1A3A5C" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
    cell.alignment = { horizontal: "left", vertical: "middle" };
  });
  row.height = 18;
};

const styleDataRow = (row) => {
  row.eachCell((cell) => {
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE0E7EF" } },
      bottom: { style: "thin", color: { argb: "FFE0E7EF" } },
      left: { style: "thin", color: { argb: "FFE0E7EF" } },
      right: { style: "thin", color: { argb: "FFE0E7EF" } },
    };
  });
};

// ─── Sheet header helper (logo + title + date + spacers + note) ───────────────

const applySheetHeader = async (workbook, worksheet, titleText, dateLabel) => {
  try {
    const response = await fetch(wg_report_logo);
    const imageBlob = await response.blob();
    const base64 = await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsDataURL(imageBlob);
    });
    const imageId = workbook.addImage({ base64, extension: "png" });
    worksheet.addImage(imageId, {
      tl: { col: 6, row: 0 },
      ext: { width: 170, height: 60 },
    });
  } catch (_) {
    // non-fatal — continue without logo if fetch fails
  }

  worksheet.getRow(1).height = 65;
  worksheet.mergeCells("A1:H1");
  const titleCell = worksheet.getCell("A1");
  titleCell.value = titleText;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: "left", vertical: "middle" };

  worksheet.getRow(2).height = 20;
  worksheet.mergeCells("A2:H2");
  const dateCell = worksheet.getCell("A2");
  dateCell.value = `Date: ${dateLabel}`;
  dateCell.font = { bold: true, size: 12 };
  dateCell.alignment = { horizontal: "left", vertical: "middle" };

  worksheet.addRow([]);
  worksheet.addRow([]);

  worksheet.mergeCells("A5:H5");
  const noteCell = worksheet.getCell("A5");
  noteCell.value =
    "Important: Gas Day dates (Column A in the table section) must follow the MM/DD/YYYY format (e.g., 05/01/2026)";
  noteCell.font = { italic: true, bold: true, color: { argb: "FF8B0000" } };
  noteCell.alignment = { horizontal: "left", vertical: "middle" };
  worksheet.getRow(5).height = 18;

  worksheet.addRow([]);
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ForecastDrvUpload = ({
  // mode
  uploadMode,
  onUploadModeChange,
  // forecast drv props
  setValue,
  watch,
  control,
  trigger,
  siCount,
  setSiCount,
  onTableRowsReset,
  tableRows,
  onRowChange,
  tableDisplayStartDate,
  tableDayCount,
  tableRowIndexOffset,
  onTableUpload,
  // forecast drv uploaded file
  forecastFileName,
  onForecastFileNameChange,
  // email attachments props
  emailAttachments,
  onEmailAttachmentsChange,
  // default attachments from API
  defaultAttachments = [],
  onDefaultAttachmentsChange,
  // options from API (used for label matching during parse)
  curtailmentOptions = [],
  interruptionTypeOptions = [],
  // api
  dispatch,
  uploadAttachmentsApi,
  deleteAttachmentsApi,
  fetchAttachmentsApi,
  downloadFileApi,
  // access control
  isAdmin,
}) => {
  const fileInputRef = useRef(null);

  // ── Pending SI rows ref ───────────────────────────────────────────────────
  const pendingSiRowsRef = useRef(null);

  // ── Delete modal state ────────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    if (!pendingSiRowsRef.current) return;
    const rows = pendingSiRowsRef.current;
    pendingSiRowsRef.current = null;

    rows.forEach(({ type, begin, end }, i) => {
      setValue(`siType_${i}`, type, { shouldDirty: true });
      setValue(`siBegin_${i}`, parseTemplateDateTime(begin), { shouldDirty: true });
      setValue(`siEnd_${i}`, parseTemplateDateTime(end), { shouldDirty: true });
    });
  }, [siCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync RHF uploadMode field → parent state ──────────────────────────────
  const watchedUploadMode = watch("uploadMode");
  useEffect(() => {
    const next = watchedUploadMode?.value ?? watchedUploadMode ?? null;
    if (next && next !== uploadMode) {
      onUploadModeChange(next);
    }
  }, [watchedUploadMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  // ── Refresh attachments list from API ─────────────────────────────────────

  const refreshAttachments = async () => {
    const res = await dispatch(fetchAttachmentsApi()).unwrap();
    const files = (res?.Data ?? []).map((item) => ({
      id: item.ID,
      name: item.FileName,
      url: item.Url,
      format: item.Format,
      size: item.Size,
      isDefault: true,
    }));
    onDefaultAttachmentsChange(files);
  };

  // ── Download default attachment ───────────────────────────────────────────

  const handleDownloadDefaultAttachment = async (file) => {
    try {
      const { url: fileUrl, name: fileName } = file;

      if (fileUrl) {
        await dispatch(downloadFileApi(fileName)).unwrap();
      } else {
        throw new Error(ERR_MSG["073"]);
      }
    } catch (err) {
      dispatch(
        alertActions.error({
          message: err.message ? ERR_MSG["073"] : err,
          header,
        })
      );
    }
  };

  // ── Delete modal handlers ─────────────────────────────────────────────────

  const handleDeleteDefaultAttachmentClick = (id) => {
    setPendingDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteDefaultAttachmentConfirm = async () => {
    setDeleteModalOpen(false);
    try {
      await dispatch(deleteAttachmentsApi({ ID: pendingDeleteId })).unwrap();
      await refreshAttachments();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setPendingDeleteId(null);
  };

  // ── Download template ─────────────────────────────────────────────────────

  const handleDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("ForecastDRV");

    const balancingCurtailment = watch("balancingCurtailment");
    const balancingBegin = watch("balancingBegin");
    const balancingEnd = watch("balancingEnd");
    // Mirror exactly what ForecastDrvTable is currently rendering (same
    // start date, day count, and row offset), so the downloaded file always
    // matches the on-screen table — including an active Filter window or a
    // Locked Date Range shift that has moved tableRows out of alignment.
    const dayCount = tableDayCount ?? tableRows.length;
    const rowIndexOffset = tableRowIndexOffset ?? 0;
    const templateDates = Array.from({ length: dayCount }, (_, i) =>
      tableDisplayStartDate
        ? dayjs(tableDisplayStartDate).add(i, "day").format("MM/DD/YYYY")
        : ""
    );

    ws.columns = [
      { width: 36 },
      { width: 30 },
      { width: 30 },
      { width: 22 },
      { width: 22 },
      { width: 22 },
      { width: 22 },
      { width: 22 },
    ];

    const dateLabel =
      templateDates.length > 0
        ? `${templateDates[0]} - ${templateDates[templateDates.length - 1]}`
        : dayjs().format("MM/DD/YYYY");

    await applySheetHeader(workbook, ws, "Forecast DRV", dateLabel);

    // ── Curtailment section ──
    const s1Label = ws.addRow([SECTION_LABELS.curtailment]);
    ws.mergeCells(`A${s1Label.number}:C${s1Label.number}`);
    styleSectionLabelRow(s1Label);

    const curtailmentHeader = ws.addRow(["Curtailment Type", "Begin", "End"]);
    styleHeaderRow(curtailmentHeader);

    const curtailmentLabel =
      curtailmentOptions.find((o) => o.value === balancingCurtailment)?.label || "";
    const curtailmentData = ws.addRow([
      curtailmentLabel,
      formatDateTime(balancingBegin),
      formatDateTime(balancingEnd),
    ]);
    styleDataRow(curtailmentData);

    ws.addRow([]);

    // ── Scheduled Interruption section ──
    const s2Label = ws.addRow([SECTION_LABELS.interruption]);
    ws.mergeCells(`A${s2Label.number}:C${s2Label.number}`);
    styleSectionLabelRow(s2Label);

    const siHeader = ws.addRow(["Interruption Type", "Begin", "End"]);
    styleHeaderRow(siHeader);

    for (let i = 0; i < siCount; i++) {
      const siType = watch(`siType_${i}`);
      const siBegin = watch(`siBegin_${i}`);
      const siEnd = watch(`siEnd_${i}`);
      const siTypeLabel =
        interruptionTypeOptions.find((o) => o.value === siType)?.label ?? siType ?? "";
      const siRow = ws.addRow([
        siTypeLabel,
        formatDateTime(siBegin),
        formatDateTime(siEnd),
      ]);
      styleDataRow(siRow);
    }
    if (siCount === 0) {
      styleDataRow(ws.addRow(["", "", ""]));
    }

    ws.addRow([]);

    // ── DRV Forecast Table section ──
    // tableRows already holds comma-formatted strings for all fields except
    // forecastedTemp, so the template will naturally show "1,000" style values.
    const s3Label = ws.addRow([SECTION_LABELS.table]);
    ws.mergeCells(`A${s3Label.number}:H${s3Label.number}`);
    styleSectionLabelRow(s3Label);

    const tableHeaderRow = ws.addRow([
      "Gas Day",
      ...TABLE_FIELDS.map((f) => f.label),
    ]);
    styleHeaderRow(tableHeaderRow);

    templateDates.forEach((date, i) => {
      const row = tableRows[rowIndexOffset + i] || {};
      styleDataRow(
        ws.addRow([
          date,
          row.forecastedTemp ?? "",
          row.totalDRV ?? "",
          row.cspFirm ?? "",
          row.cspInterruptible ?? "",
          row.wglSendout ?? "",
        ])
      );
    });

    ws.addRow([]);
    const noteRow = ws.addRow([
      `Note: Gas Day dates must be ${dayCount} sequential days in MM/DD/YYYY format.`,
    ]);
    ws.mergeCells(`A${noteRow.number}:H${noteRow.number}`);
    noteRow.getCell(1).font = { italic: true, color: { argb: "FF8B0000" } };

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "DRV_Forecast.xlsx"
    );
  };

  // ── Reset all fields before parsing a new file ────────────────────────────

  const resetFields = (currentSiCount) => {
    setValue("balancingCurtailment", null);
    setValue("balancingBegin", null);
    setValue("balancingEnd", null);

    for (let i = 0; i < currentSiCount; i++) {
      setValue(`siType_${i}`, null);
      setValue(`siBegin_${i}`, null);
      setValue(`siEnd_${i}`, null);
    }
    setSiCount(1);

    onTableRowsReset();
    onForecastFileNameChange(null);
  };

  // ── Upload handler ────────────────────────────────────────────────────────

  const handleChange = async (event) => {
    // Guard: non-admins should not be able to upload
    if (!isAdmin) return;

    event.preventDefault();
    const filesAdded = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files;

    if (!filesAdded || filesAdded.length === 0) return;

    if (uploadMode === "emails_attachment") {
      const oversizedFiles = Array.from(filesAdded).filter(
        (f) => f.size > MAX_FILE_SIZE
      );
      if (oversizedFiles.length > 0) {
        dispatch(
          alertActions.error({
            message: `File size cannot exceed 5MB`,
            header,
          })
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      const invalidFiles = Array.from(filesAdded).filter((f) => {
        const ext = f.name.split(".").pop().toLowerCase();
        return !["xlsx", "xls", "xlsm", "pdf"].includes(ext);
      });
      if (invalidFiles.length > 0) {
        dispatch(
          alertActions.error({
            message: `Invalid file format.`,
            header,
          })
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      const validFiles = Array.from(filesAdded).filter((f) => {
        const ext = f.name.split(".").pop().toLowerCase();
        return ["xlsx", "xls", "xlsm", "pdf"].includes(ext);
      });

      if (validFiles.length > 0) {
        try {
          const formData = new FormData();
          validFiles.forEach((file) => formData.append("FileData", file));
          await dispatch(uploadAttachmentsApi(formData)).unwrap();
          await refreshAttachments();
        } catch (err) {
          console.error("Upload failed:", err);
        }
      }
    } else {
      const file = filesAdded[0];
      const ext = file.name.split(".").pop().toLowerCase();

      if (!["xlsx", "xls", "xlsm"].includes(ext)) {
        dispatch(
          alertActions.error({
            message: `Invalid file format.`,
            header,
          })
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      resetFields(siCount);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const warnings = parseWorkbook(e.target.result);
          onForecastFileNameChange(file.name);
          if (warnings.length > 0) {
            dispatch(
              alertActions.error({
                message: warnings.join("\n"),
                header,
              })
            );
          }
        } catch (err) {
          console.error(err);
          dispatch(
            alertActions.error({
              message: ERR_MSG["073"],
              header,
            })
          );
        }
      };
      reader.readAsArrayBuffer(file);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Parse workbook (Forecast DRV only) ───────────────────────────────────

  const parseWorkbook = (arrayBuffer) => {
    const warnings = [];

    const wb = XLSX.read(arrayBuffer, { type: "array" });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
    });

    const findSection = (label) =>
      rows.findIndex((r) =>
        String(r[0] ?? "")
          .trim()
          .toLowerCase()
          .includes(label.toLowerCase())
      );

    const curtailmentSectionIdx = findSection("balancing curtailment");
    const siSectionIdx = findSection("scheduled interruption");
    // Matches both the current template header ("DRV Forecast Table") and
    // any older downloaded template still in circulation ("N-Day DRV
    // Forecast"), so re-uploading a template downloaded before this label
    // changed still works.
    const tableSectionIdx = rows.findIndex((r) => {
      const cell = String(r[0] ?? "").trim().toLowerCase();
      return cell.includes("drv forecast table") || /\d+-day drv forecast/.test(cell);
    });

    // ── Curtailment section ──
    if (curtailmentSectionIdx !== -1) {
      const dataRow = rows[curtailmentSectionIdx + 2];
      if (dataRow) {
        const curtailmentTypeRaw = String(dataRow[0] ?? "").trim();
        if (curtailmentTypeRaw) {
          const matched = curtailmentOptions.find(
            (o) => o.label.toLowerCase() === curtailmentTypeRaw.toLowerCase()
          );
          setValue("balancingCurtailment", matched ? matched.value : null);
          if (!matched) {
            warnings.push(
              `Curtailment Type "${curtailmentTypeRaw}" was not recognized and was left blank.`
            );
          }
        } else {
          setValue("balancingCurtailment", null);
        }
        setValue("balancingBegin", parseTemplateDateTime(String(dataRow[1] ?? "").trim()));
        setValue("balancingEnd", parseTemplateDateTime(String(dataRow[2] ?? "").trim()));
      }
    }

    // ── Scheduled Interruption section ──
    if (siSectionIdx !== -1) {
      const dataStartIdx = siSectionIdx + 2;
      const nextSectionIdx = tableSectionIdx !== -1 ? tableSectionIdx : rows.length;

      const siDataRows = [];
      for (let i = dataStartIdx; i < nextSectionIdx; i++) {
        const r = rows[i];
        if (!r || r.every((c) => String(c).trim() === "")) break;
        siDataRows.push(r);
      }

      const validSiRows = [];
      siDataRows.forEach((r, idx) => {
        const typeRaw = String(r[0] ?? "").trim();
        if (!typeRaw) return;
        const matched = interruptionTypeOptions.find(
          (o) => o.label.toLowerCase() === typeRaw.toLowerCase()
        );
        if (matched) {
          validSiRows.push({ type: matched.value, begin: r[1], end: r[2] });
        } else {
          warnings.push(
            `Scheduled Interruption row ${idx + 1}: type "${typeRaw}" was not recognized and was skipped.`
          );
        }
      });

      if (validSiRows.length > 0) {
        if (validSiRows.length === siCount) {
          validSiRows.forEach(({ type, begin, end }, i) => {
            setValue(`siType_${i}`, type, { shouldDirty: true });
            setValue(`siBegin_${i}`, parseTemplateDateTime(begin), { shouldDirty: true });
            setValue(`siEnd_${i}`, parseTemplateDateTime(end), { shouldDirty: true });
          });
        } else {
          pendingSiRowsRef.current = validSiRows;
          setSiCount(validSiRows.length);
        }
      }
    } else {
      warnings.push(
        "Scheduled Interruption section not found in the uploaded file — interruptions were not updated."
      );
    }

    // ── DRV Forecast Table section ──
    // Everything about the table (day count, dates, values) is derived
    // purely from the uploaded file, not from whatever the on-screen table
    // currently shows — so a valid file is never rejected just because its
    // day count differs from what's currently displayed. Accepts numbers
    // with or without thousands separators (e.g. "1,000" or "1000");
    // commas are stripped then re-applied, except for Forecasted Temp.
    if (tableSectionIdx === -1) {
      warnings.push(
        "DRV Forecast Table section not found in the uploaded file — table was not updated."
      );
    } else {
      const dataStartIdx = tableSectionIdx + 2;
      const MAX_TABLE_ROWS = 366;

      // Scan forward from the header for a contiguous run of parseable
      // Gas Day dates, stopping at the first blank/unparseable row.
      const candidateRows = [];
      for (let i = dataStartIdx; i < Math.min(rows.length, dataStartIdx + MAX_TABLE_ROWS); i++) {
        const r = rows[i];
        if (!r || r.every((c) => String(c).trim() === "")) break;
        const gasDay = parseGasDay(r[0]);
        if (!gasDay) break;
        candidateRows.push({ row: r, gasDay });
      }

      if (candidateRows.length === 0) {
        warnings.push(
          "No valid Gas Day rows were found in the DRV Forecast Table section — table was not updated."
        );
      } else {
        // Keep only the leading run that's exactly sequential from the
        // first date; truncate (rather than reject everything) on a break.
        let sequentialCount = 1;
        while (
          sequentialCount < candidateRows.length &&
          candidateRows[sequentialCount].gasDay.diff(
            candidateRows[sequentialCount - 1].gasDay,
            "day"
          ) === 1
        ) {
          sequentialCount++;
        }

        if (sequentialCount < candidateRows.length) {
          warnings.push(
            `Only the first ${sequentialCount} sequential Gas Day row(s) were applied; the remaining rows were ignored.`
          );
        }

        const parsedRows = candidateRows.slice(0, sequentialCount).map(({ row: r }) => {
          const parsedRow = {};
          TABLE_FIELDS.forEach((field, colIdx) => {
            const rawValue = String(r[colIdx + 1] ?? "").trim();
            if (field.allowDecimal) {
              // Forecasted Temperature: free text, no sanitizing/formatting applied (matches manual entry in ForecastDrvTable.jsx).
              parsedRow[field.key] = rawValue;
            } else {
              const cleaned = stripCommas(rawValue).replace(/[^0-9-]/g, "");
              parsedRow[field.key] = formatThousands(cleaned);
            }
          });
          return parsedRow;
        });

        if (onTableUpload) {
          onTableUpload({
            startDate: candidateRows[0].gasDay,
            rows: parsedRows,
          });
        }
      }
    }

    return warnings;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const isForecastMode = uploadMode === "forecast_drv";

  return (
    <Typography component="div" className="UploadContainer" sx={disabledFieldSx}>
      <Grid container spacing={3}>

        {/* ── Mode dropdown — disabled when not admin ── */}
        <Grid item xs={12}>
          <AutocompleteInput
            control={control}
            trigger={trigger}
            name="uploadMode"
            label="Upload Type"
            options={UPLOAD_MODE_OPTIONS}
            disabled={!isAdmin}
          />
        </Grid>

        {/* ── Upload button — hidden when not admin ── */}
        {isAdmin && (
          <Grid item xs={12}>
            <Button
              component="label"
              variant="contained"
              className="Uploadfiles"
              startIcon={<img src={materialsymbolsupload} alt="Upload" />}
            >
              <span>Upload your files here</span>
              <VisuallyHiddenInput
                type="file"
                multiple={!isForecastMode}
                onChange={handleChange}
                ref={fileInputRef}
              />
            </Button>
          </Grid>
        )}

        {/* ── Supported formats ── */}
        <Grid item xs={12}>
          <Typography component="div" className="SupportedFormats">
            <Typography component="h3">Supported Formats</Typography>
            <Typography component="div" className="fileformatlist">
              {(isForecastMode ? supportedFormats : emailAttachmentFormats).map((fmt) => (
                <span key={fmt}>{fmt} </span>
              ))}
            </Typography>
          </Typography>
        </Grid>

        {/* ── Download Template (Forecast DRV only) — always visible, anyone can download ── */}
        {isForecastMode && (
          <Grid item xs={12}>
            <Typography component="div" className="SupportedFormats">
              <Typography component="h3">Download Template</Typography>
              <div className="mar-top-16">
                <Typography component="div">
                  Sample File Format
                  <IconButton onClick={handleDownload}>
                    <img src={materialsymbolsdownload} alt="material-symbols_download" />
                  </IconButton>
                </Typography>
              </div>
            </Typography>
          </Grid>
        )}

        {/* ── Uploaded file (Forecast DRV) — show name, hide delete when not admin ── */}
        {isForecastMode && forecastFileName && (
          <Grid item xs={12}>
            <Typography component="div" className="SupportedFormats">
              <Typography component="h3">Uploaded File</Typography>
              <Typography
                component="div"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span>{forecastFileName}</span>
                {/* Delete button hidden when not admin */}
                {isAdmin && (
                  <IconButton
                    size="small"
                    onClick={() => onForecastFileNameChange(null)}
                    sx={{ color: "#c0392b", ml: 1 }}
                  >
                    <img src={Delete} alt="Delete" />
                  </IconButton>
                )}
              </Typography>
            </Typography>
          </Grid>
        )}

        {/* ── Email attachments list ── */}
        {!isForecastMode &&
          (defaultAttachments.length > 0 || emailAttachments.length > 0) && (
            <Grid item xs={12}>
              <Typography component="div" className="SupportedFormats">
                <Typography component="h3">Email Attachment</Typography>

                {/* Default attachments from API */}
                {defaultAttachments.map((file) => (
                  <Typography
                    key={`default-${file.id}`}
                    component="div"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 13,
                      marginTop: 4,
                    }}
                  >
                    <span>{file.name}</span>
                    <span style={{ display: "flex", alignItems: "center" }}>
                      {/* Download always visible */}
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadDefaultAttachment(file)}
                        sx={{ ml: 1 }}
                      >
                        <img src={materialsymbolsdownload} alt="Download" />
                      </IconButton>
                      {/* Delete button hidden when not admin */}
                      {isAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDefaultAttachmentClick(file.id)}
                          sx={{ color: "#c0392b", ml: 0.5 }}
                        >
                          <img src={Delete} alt="Delete" />
                        </IconButton>
                      )}
                    </span>
                  </Typography>
                ))}

                {/* User-uploaded attachments staged locally */}
                {emailAttachments.map((file, idx) => (
                  <Typography
                    key={`${file.name}-${idx}`}
                    component="div"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 13,
                      marginTop: 4,
                    }}
                  >
                    <span>{file.name}</span>
                    {/* Delete button hidden when not admin */}
                    {isAdmin && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          onEmailAttachmentsChange((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                        sx={{ color: "#c0392b", ml: 1 }}
                      >
                        <img src={Delete} alt="Delete" />
                      </IconButton>
                    )}
                  </Typography>
                ))}
              </Typography>
            </Grid>
          )}
      </Grid>

      {/* ── Delete confirmation modal ── */}
      {deleteModalOpen && (
        <ModalPopup
          header={"Delete Attachment"}
          message1={"Are you sure you want to delete this attachment?"}
          btnPrimaryText={"Confirm"}
          btnSecondaryText={"Cancel"}
          handlePrimaryClick={handleDeleteDefaultAttachmentConfirm}
          handleSecondaryClick={handleDeleteModalClose}
        />
      )}
    </Typography>
  );
};

export default ForecastDrvUpload;